import type { InvestmentsReportTemplate } from "@eu.mdotm.api/sphere";
import {
	type CompositionColumnPreference,
	type ReportTemplateStatus,
	type ReportTemplateVariant,
	type ReportTemplateItem,
	type ReportTemplateItemMap,
	latestVersion,
} from "$root/functional-areas/reports/report-latest";
import { endingPageFallback } from "$root/pages/PortfolioStudioSettings/ReportEditor/ending-page-fallback";

import { generateUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";

export const DefaultReportTemplateName = {
	sphere: "sphere",
	custom: "custom",
} as const;

export type DefaultReportTemplateName = (typeof DefaultReportTemplateName)[keyof typeof DefaultReportTemplateName];

export type DefaultReportTemplate = {
	id: DefaultReportTemplateName;
	templateName: string;
	format: "pdf" | "xlsx";
	visible: boolean;
	data: {
		status: ReportTemplateStatus;
		version: number;
		templateItemsByVariant: Record<ReportTemplateVariant, Array<ReportTemplateItem>>;
		templateDraftItemsByVariant: Record<ReportTemplateVariant, Array<ReportTemplateItem>>;
	};
};
export const defaultTemplateName = "Untitled template";

const baseCompositionColumn = {
	name: {
		enabled: true,
		preferenceType: {
			classificationUuid: "NAME",
			label: "Name",
		},
	},
	weight: {
		enabled: true,
		preferenceType: {
			classificationUuid: "WEIGHT",
			label: "Weight",
		},
	},
	description: {
		enabled: true,
		preferenceType: {
			classificationUuid: "DESCRIPTION",
			label: "Description",
		},
	},
	enhancedWeight: {
		enabled: true,
		preferenceType: {
			classificationUuid: "ENHANCED_WEIGHT",
			label: "Enhanced weight",
		},
	},
	alias: {
		enabled: true,
		preferenceType: {
			classificationUuid: "ALIAS",
			label: "Alias",
		},
	},
	difference: {
		enabled: true,
		preferenceType: {
			classificationUuid: "DIFFERENCE",
			label: "Delta",
		},
	},
} satisfies Record<string, CompositionColumnPreference>;

const defaultPortfolioMetricsPreferences = [
	{
		enabled: true,
		metricType: "VOLATILITY_SINCE_INCEPTION",
	},
	{
		enabled: true,
		metricType: "VOLATILITY_YTD",
	},
	{
		enabled: true,
		metricType: "VOLATILITY_1M",
	},
	{
		enabled: true,
		metricType: "VOLATILITY_3M",
	},
	{
		enabled: true,
		metricType: "VOLATILITY_6M",
	},
	{
		enabled: true,
		metricType: "VOLATILITY_1Y",
	},
	{
		enabled: true,
		metricType: "EFFICIENCY_RATIO_SINCE_INCEPTION",
	},
	{
		enabled: true,
		metricType: "EFFICIENCY_RATIO_YTD",
	},
	{
		enabled: true,
		metricType: "EFFICIENCY_RATIO_1M",
	},
	{
		enabled: true,
		metricType: "EFFICIENCY_RATIO_3M",
	},
	{
		enabled: true,
		metricType: "EFFICIENCY_RATIO_6M",
	},
	{
		enabled: true,
		metricType: "EFFICIENCY_RATIO_1Y",
	},
	{
		enabled: true,
		metricType: "MAX_DRAWDOWN_SINCE_INCEPTION",
	},
	{
		enabled: true,
		metricType: "MAX_DRAWDOWN_YTD",
	},
	{
		enabled: true,
		metricType: "MAX_DRAWDOWN_1M",
	},
	{
		enabled: true,
		metricType: "MAX_DRAWDOWN_3M",
	},
	{
		enabled: true,
		metricType: "MAX_DRAWDOWN_6M",
	},
	{
		enabled: true,
		metricType: "MAX_DRAWDOWN_1Y",
	},
	{
		enabled: true,
		metricType: "PERFORMANCE_SINCE_INCEPTION",
	},
	{
		enabled: true,
		metricType: "PERFORMANCE_YTD",
	},
	{
		enabled: true,
		metricType: "PERFORMANCE_1M",
	},
	{
		enabled: true,
		metricType: "PERFORMANCE_3M",
	},
	{
		enabled: true,
		metricType: "PERFORMANCE_6M",
	},
	{
		enabled: true,
		metricType: "PERFORMANCE_1Y",
	},
	{
		enabled: true,
		metricType: "SORTINO_SINCE_INCEPTION",
	},
	{
		enabled: true,
		metricType: "SORTINO_YTD",
	},
	{
		enabled: true,
		metricType: "SORTINO_1M",
	},
	{
		enabled: true,
		metricType: "SORTINO_3M",
	},
	{
		enabled: true,
		metricType: "SORTINO_6M",
	},
	{
		enabled: true,
		metricType: "SORTINO_1Y",
	},
] satisfies ReportTemplateItemMap["portfolioMetrics"]["preferences"];

const sphereTemplateDefault: Record<ReportTemplateVariant, Array<ReportTemplateItem>> = {
	current: [
		{
			id: generateUniqueDOMId(),
			kind: "cover",
			title: "PORTFOLIO DETAILS",
		},
		{
			id: generateUniqueDOMId(),
			kind: "summary",
			standardFields: { baseCurrency: true, benchmark: true, portfolioName: true, reference: true, universe: true },
			customAttributes: undefined,
		},
		{
			id: generateUniqueDOMId(),
			kind: "portfolioMetrics",
			showBenchmark: true,
			preferences: defaultPortfolioMetricsPreferences,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exAntePortfolioMetrics",
			showBenchmark: true,
			preferences: [
				{ metricType: "TRACKING_ERROR_3Y", enabled: true },
				{ metricType: "RETURN_3Y", enabled: true },
				{ metricType: "MAX_DRAWDOWN_3Y", enabled: true },
				{ metricType: "VOLATILITY_3Y", enabled: true },
				{ metricType: "EFFICIENCY_RATIO_3Y", enabled: true },
				{ metricType: "DIVERSIFICATION_RATIO_3Y", enabled: true },
				{ metricType: "HISTORICAL_VAR_95_3Y", enabled: true },
				{ metricType: "VAR", enabled: true },
				{ metricType: "VOLATILITY", enabled: true },
				{ metricType: "TRACKING_ERROR", enabled: true },
			],
		},
		{
			id: generateUniqueDOMId(),
			kind: "performance",
			benchmark: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "commentary",
			useTemplateNameAsTitle: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "composition",
			currentColumnPreferences: [baseCompositionColumn.name, baseCompositionColumn.alias, baseCompositionColumn.weight],
			proposalColumnPreferences: [
				baseCompositionColumn.name,
				baseCompositionColumn.alias,
				baseCompositionColumn.weight,
				baseCompositionColumn.enhancedWeight,
				baseCompositionColumn.difference,
			],
			decimalPlaces: 2,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exposure",
			selectedCategories: [
				{
					label: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
					type: "RISK_MODEL",
					value: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
				},
				{
					label: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
					type: "RISK_MODEL",
					value: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
				},
			],
		},
		{
			id: generateUniqueDOMId(),
			kind: "performanceAttribution",
			FROM_INCEPTION: false,
			YEAR_TO_DATE: true,
			ONE_MONTH: false,
			THREE_MONTHS: false,
			SIX_MONTHS: false,
			ONE_YEAR: false,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exAnteContributionVolatility",
			categories: {
				MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: true,
				MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: true,
				MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: false,
				MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
				MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: false,
			},
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "performanceAndVolatilityContribution",
			categories: {
				MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: true,
				MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: true,
				MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: false,
				MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: false,
				MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
				COMPOSITION: false,
			},
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "factorExposure",
			benchmark: true,
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "disclaimer",
			contact: {
				description: `${endingPageFallback.address}\n\n\n${endingPageFallback.mail}\n${endingPageFallback.website}`,
				enabled: true,
			},
			content: {
				title: endingPageFallback.disclaimer.title,
				description: endingPageFallback.disclaimer.description,
				enabled: true,
			},
		},
	],
	proposal: [
		{
			id: generateUniqueDOMId(),
			kind: "cover",
			title: "PORTFOLIO DETAILS",
		},
		{
			id: generateUniqueDOMId(),
			kind: "summary",
			standardFields: {
				baseCurrency: true,
				benchmark: true,
				portfolioName: true,
				reference: true,
				universe: true,
			},
			customAttributes: undefined,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exAntePortfolioMetrics",
			showBenchmark: true,
			preferences: [
				{ metricType: "TRACKING_ERROR_3Y", enabled: true },
				{ metricType: "RETURN_3Y", enabled: true },
				{ metricType: "MAX_DRAWDOWN_3Y", enabled: true },
				{ metricType: "VOLATILITY_3Y", enabled: true },
				{ metricType: "EFFICIENCY_RATIO_3Y", enabled: true },
				{ metricType: "DIVERSIFICATION_RATIO_3Y", enabled: true },
				{ metricType: "HISTORICAL_VAR_95_3Y", enabled: true },
				{ metricType: "VAR", enabled: true },
				{ metricType: "VOLATILITY", enabled: true },
				{ metricType: "TRACKING_ERROR", enabled: true },
			],
		},
		{
			id: generateUniqueDOMId(),
			kind: "commentary",
			useTemplateNameAsTitle: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "composition",
			currentColumnPreferences: [baseCompositionColumn.name, baseCompositionColumn.alias, baseCompositionColumn.weight],
			proposalColumnPreferences: [
				baseCompositionColumn.name,
				baseCompositionColumn.alias,
				baseCompositionColumn.weight,
				baseCompositionColumn.enhancedWeight,
				baseCompositionColumn.difference,
			],
			decimalPlaces: 2,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exposure",
			selectedCategories: [
				{
					label: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
					type: "RISK_MODEL",
					value: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
				},
				{
					label: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
					type: "RISK_MODEL",
					value: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
				},
			],
		},
		{
			id: generateUniqueDOMId(),
			kind: "exAnteContributionVolatility",
			categories: {
				MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: true,
				MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: true,
				MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: false,
				MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
				MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: false,
			},
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "factorExposure",
			benchmark: true,
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "disclaimer",
			contact: {
				description: `${endingPageFallback.address}\n\n\n${endingPageFallback.mail}\n${endingPageFallback.website}`,
				enabled: true,
			},
			content: {
				title: endingPageFallback.disclaimer.title,
				description: endingPageFallback.disclaimer.description,
				enabled: true,
			},
		},
	],
};

const fallbackTemplate: Record<ReportTemplateVariant, Array<ReportTemplateItem>> = {
	current: [
		{
			id: generateUniqueDOMId(),
			kind: "cover",
			title: "PORTFOLIO DETAILS",
		},
		{
			id: generateUniqueDOMId(),
			kind: "summary",
			standardFields: {
				baseCurrency: true,
				benchmark: true,
				portfolioName: true,
				reference: true,
				universe: true,
			},
			customAttributes: undefined,
		},
		{
			id: generateUniqueDOMId(),
			kind: "portfolioMetrics",
			showBenchmark: true,
			preferences: defaultPortfolioMetricsPreferences,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exAntePortfolioMetrics",
			showBenchmark: true,
			preferences: [
				{ metricType: "TRACKING_ERROR_3Y", enabled: true },
				{ metricType: "RETURN_3Y", enabled: true },
				{ metricType: "MAX_DRAWDOWN_3Y", enabled: true },
				{ metricType: "VOLATILITY_3Y", enabled: true },
				{ metricType: "EFFICIENCY_RATIO_3Y", enabled: true },
				{ metricType: "DIVERSIFICATION_RATIO_3Y", enabled: true },
				{ metricType: "HISTORICAL_VAR_95_3Y", enabled: true },
				{ metricType: "VAR", enabled: true },
				{ metricType: "VOLATILITY", enabled: true },
				{ metricType: "TRACKING_ERROR", enabled: true },
			],
		},
		{
			id: generateUniqueDOMId(),
			kind: "performance",
			benchmark: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "commentary",
			useTemplateNameAsTitle: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "composition",
			currentColumnPreferences: [baseCompositionColumn.name, baseCompositionColumn.alias, baseCompositionColumn.weight],
			proposalColumnPreferences: [
				baseCompositionColumn.name,
				baseCompositionColumn.alias,
				baseCompositionColumn.weight,
				baseCompositionColumn.enhancedWeight,
				baseCompositionColumn.difference,
			],
			decimalPlaces: 2,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exposure",
			selectedCategories: [
				{
					label: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
					type: "RISK_MODEL",
					value: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
				},
				{
					label: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
					type: "RISK_MODEL",
					value: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
				},
			],
		},
		{
			id: generateUniqueDOMId(),
			kind: "performanceAttribution",
			FROM_INCEPTION: false,
			YEAR_TO_DATE: true,
			ONE_MONTH: false,
			THREE_MONTHS: false,
			SIX_MONTHS: false,
			ONE_YEAR: false,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exAnteContributionVolatility",
			categories: {
				MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: true,
				MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: true,
				MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: false,
				MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
				MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: false,
			},
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "performanceAndVolatilityContribution",
			categories: {
				MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: true,
				MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: true,
				MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: false,
				MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: false,
				MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
				COMPOSITION: false,
			},
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "factorExposure",
			benchmark: true,
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "disclaimer",
			contact: { enabled: false },
			content: { enabled: false },
		},
	],
	proposal: [
		{
			id: generateUniqueDOMId(),
			kind: "cover",
			title: "PORTFOLIO DETAILS",
		},
		{
			id: generateUniqueDOMId(),
			kind: "summary",
			standardFields: { baseCurrency: true, benchmark: true, portfolioName: true, reference: true, universe: true },
			customAttributes: undefined,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exAntePortfolioMetrics",
			showBenchmark: true,
			preferences: [
				{ metricType: "TRACKING_ERROR_3Y", enabled: true },
				{ metricType: "RETURN_3Y", enabled: true },
				{ metricType: "MAX_DRAWDOWN_3Y", enabled: true },
				{ metricType: "VOLATILITY_3Y", enabled: true },
				{ metricType: "EFFICIENCY_RATIO_3Y", enabled: true },
				{ metricType: "DIVERSIFICATION_RATIO_3Y", enabled: true },
				{ metricType: "HISTORICAL_VAR_95_3Y", enabled: true },
				{ metricType: "VAR", enabled: true },
				{ metricType: "VOLATILITY", enabled: true },
				{ metricType: "TRACKING_ERROR", enabled: true },
			],
		},
		{
			id: generateUniqueDOMId(),
			kind: "commentary",
			useTemplateNameAsTitle: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "composition",
			currentColumnPreferences: [baseCompositionColumn.name, baseCompositionColumn.alias, baseCompositionColumn.weight],
			proposalColumnPreferences: [
				baseCompositionColumn.name,
				baseCompositionColumn.alias,
				baseCompositionColumn.weight,
				baseCompositionColumn.enhancedWeight,
				baseCompositionColumn.difference,
			],
			decimalPlaces: 2,
		},
		{
			id: generateUniqueDOMId(),
			kind: "exposure",
			selectedCategories: [
				{
					label: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
					type: "RISK_MODEL",
					value: "MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
				},
				{
					label: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
					type: "RISK_MODEL",
					value: "MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
				},
			],
		},
		{
			id: generateUniqueDOMId(),
			kind: "exAnteContributionVolatility",
			categories: {
				MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: true,
				MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: true,
				MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: false,
				MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: false,
				MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: false,
			},
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "factorExposure",
			benchmark: true,
			filterSignificant: true,
		},
		{
			id: generateUniqueDOMId(),
			kind: "disclaimer",
			contact: {
				enabled: false,
			},
			content: {
				enabled: false,
			},
		},
	],
};

export const defaultPortfolioTemplates: Array<Omit<DefaultReportTemplate, "visible">> = [
	{
		id: DefaultReportTemplateName.sphere,
		templateName: "Sphere template",
		format: "pdf",
		data: {
			version: latestVersion,
			status: "ready",
			templateItemsByVariant: sphereTemplateDefault,
			templateDraftItemsByVariant: sphereTemplateDefault,
		},
	},
];

export const startingTemplate = {
	id: "fallback",
	templateName: "Untitled",
	format: "pdf",
	data: {
		version: latestVersion,
		status: "ready",
		templateItemsByVariant: fallbackTemplate,
		templateDraftItemsByVariant: fallbackTemplate,
	},
} satisfies Omit<DefaultReportTemplate, "visible" | "id"> & { id: string };

export function isDefaultReportTemplate(
	obj: InvestmentsReportTemplate | DefaultReportTemplate,
): obj is DefaultReportTemplate {
	return !("uuid" in obj);
}

export function extractWidgetConfig<
	T extends { data: { templateItemsByVariant: Record<ReportTemplateVariant, Array<ReportTemplateItem>> } },
	TKind extends ReportTemplateItem["kind"],
>(template: T, widgetName: TKind): Extract<ReportTemplateItem, { kind: TKind }> | undefined {
	for (const items of Object.values(template.data.templateItemsByVariant)) {
		const match = items.find((item) => item.kind === widgetName);
		if (match !== undefined) {
			return match as Extract<ReportTemplateItem, { kind: TKind }>;
		}
	}
	return undefined;
}
